import React, { useEffect, useState } from 'react';
import { friendlyPastTime } from '../../lib/friendlyTime';

const API = process.env.REACT_APP_API_URL;

const STATUS_STYLES = {
  draft:       'bg-slate-50 text-slate-700 border-slate-200',
  sent:        'bg-sky-50 text-sky-700 border-sky-200',
  in_progress: 'bg-amber-50 text-amber-700 border-amber-200',
  completed:   'bg-emerald-50 text-emerald-700 border-emerald-200',
  expired:     'bg-red-50 text-red-700 border-red-200',
  cancelled:   'bg-slate-100 text-slate-500 border-slate-300',
};

export default function RequestsList({ onOpen }) {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    setLoading(true);
    const q = filter ? `?status=${encodeURIComponent(filter)}` : '';
    fetch(`${API}/api/universe-requests${q}`, { credentials: 'include' })
      .then(r => r.json())
      .then(d => setRequests(d.requests || []))
      .catch(() => setRequests([]))
      .finally(() => setLoading(false));
  }, [filter]);

  if (loading) return <div className="text-sm text-slate-500">Loading requests…</div>;
  if (requests.length === 0) {
    return (
      <div className="bg-white border border-slate-200 p-8 text-center">
        <div className="text-xs font-mono uppercase tracking-widest text-slate-500 mb-2">No requests yet</div>
        <div className="text-sm text-slate-700 mb-4">
          Click <span className="font-semibold">New Request</span> to ask an FDR for one or more universes.
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-4 flex gap-2 items-center">
        <span className="text-xs font-mono uppercase tracking-widest text-slate-500">Filter</span>
        {['', 'draft', 'sent', 'in_progress', 'completed', 'cancelled'].map(s => (
          <button
            key={s || 'all'}
            onClick={() => setFilter(s)}
            className={`text-xs font-mono px-2 py-1 border ${
              filter === s ? 'border-red-700 text-red-700' : 'border-slate-300 text-slate-600 hover:border-slate-500'
            }`}
          >
            {s || 'all'}
          </button>
        ))}
      </div>

      <table className="w-full text-sm">
        <thead className="text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
          <tr>
            <th className="text-left py-2">Title</th>
            <th className="text-left py-2">FDR</th>
            <th className="text-left py-2">Purpose</th>
            <th className="text-left py-2">Period</th>
            <th className="text-right py-2 w-20">Items</th>
            <th className="text-left py-2 w-32 pl-3">Status</th>
            <th className="text-left py-2 pl-3 w-40">When</th>
          </tr>
        </thead>
        <tbody>
          {requests.map(r => (
            <tr
              key={r.id}
              onClick={() => onOpen(r.id)}
              className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
            >
              <td className="py-2 font-semibold text-slate-900">{r.title}</td>
              <td className="py-2 text-slate-700">{r.fdr_name}</td>
              <td className="py-2 text-xs font-mono text-slate-500">{prettyPurpose(r.purpose)}</td>
              <td className="py-2 text-xs font-mono text-slate-500">
                {r.reporting_period_start && r.reporting_period_end
                  ? `${r.reporting_period_start} → ${r.reporting_period_end}` : '—'}
              </td>
              <td className="py-2 text-right font-mono text-slate-700">
                {r.received_count + r.accepted_count}/{r.item_count}
              </td>
              <td className="py-2 pl-3">
                <span className={`text-[10px] font-mono uppercase tracking-wider border px-1.5 py-0.5 ${STATUS_STYLES[r.status]}`}>
                  {r.status}
                </span>
              </td>
              <td className="py-2 pl-3 text-xs text-slate-500">
                <span title={r.created_at ? new Date(r.created_at).toLocaleString() : undefined}>
                  {friendlyPastTime(r.created_at)}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function prettyPurpose(p) {
  return ({
    audit_prep: 'Audit prep',
    quarterly_reporting: 'Quarterly reporting',
    annual_reporting: 'Annual reporting',
    ad_hoc: 'Ad-hoc',
  })[p] || p;
}
